import { useState, useMemo, useCallback } from 'react';
import { Table, Space, Select, Tag, Button, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import type { ContentReportResponse } from '@wisecure/api-client';
import type { PaginatedResponse, ModerationContentGroup } from '@/types/dashboard';
import { PageHeader } from '@/components/PageHeader';
import { ReportDetailDrawer } from '@/components/moderation/ReportDetailDrawer';
import { groupReports } from '@/pages/moderation/groupReports';
import { queryKeys } from '@/api/query-keys';
import { error as errorColors, warning, success, info, neutral } from '@/theme';

const { Text } = Typography;

const TYPE_LABELS: Record<string, string> = {
  POST: 'Publication',
  NEIGHBORING_REPORT: 'Signalement voisinage',
  NEIGHBORING_REPORT_COMMENT: 'Commentaire',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'En attente',
  REVIEWED: 'Examiné',
  RESOLVED: 'Résolu',
  DISMISSED: 'Rejeté',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: warning[400],
  REVIEWED: info[400],
  RESOLVED: success[400],
  DISMISSED: neutral[300],
};

const REASON_LABELS: Record<string, string> = {
  SPAM: 'Spam',
  HARASSMENT: 'Harcèlement',
  INAPPROPRIATE_CONTENT: 'Contenu inapproprié',
  MISINFORMATION: 'Désinformation',
  VIOLENCE: 'Violence',
  HATE_SPEECH: 'Discours haineux',
  OTHER: 'Autre',
};

interface ModerationFilters {
  status?: 'PENDING' | 'REVIEWED' | 'RESOLVED' | 'DISMISSED';
  reportableType?: 'POST' | 'NEIGHBORING_REPORT' | 'NEIGHBORING_REPORT_COMMENT';
  reason?: 'SPAM' | 'HARASSMENT' | 'INAPPROPRIATE_CONTENT' | 'MISINFORMATION' | 'VIOLENCE' | 'HATE_SPEECH' | 'OTHER';
  sortBy: 'createdAt' | 'updatedAt' | 'status' | 'reportCount';
  sortOrder: 'asc' | 'desc';
  limit: number;
  page: number;
}

export function ModerationPage() {
  const [filters, setFilters] = useState<ModerationFilters>({
    status: 'PENDING',
    sortBy: 'reportCount',
    sortOrder: 'desc',
    limit: 50,
    page: 1,
  });

  const [selectedGroup, setSelectedGroup] = useState<ModerationContentGroup | null>(null);

  const updateFilter = useCallback(
    <K extends keyof ModerationFilters>(key: K, value: ModerationFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value, ...(key !== 'page' ? { page: 1 } : {}) }));
    },
    [],
  );

  const { data, isLoading } = useWisecureQuery<PaginatedResponse<ContentReportResponse>>(
    queryKeys.contentReports(filters as unknown as Record<string, unknown>),
    (client) =>
      client.contentReports.contentReportsControllerFindAll({
        ...filters,
      }) as Promise<PaginatedResponse<ContentReportResponse>>,
  );

  const groups = useMemo(() => groupReports(data?.data ?? []), [data]);

  const columns = [
    {
      title: 'Type',
      key: 'type',
      width: 160,
      render: (_: unknown, record: ModerationContentGroup) => (
        <Tag color="blue">{TYPE_LABELS[record.reportableType] ?? record.reportableType}</Tag>
      ),
    },
    {
      title: 'Contenu',
      key: 'content',
      render: (_: unknown, record: ModerationContentGroup) => {
        const preview = record.contentPreview;
        if (!preview) return <Text type="secondary">—</Text>;
        const title = preview.title ? String(preview.title) : null;
        const desc = preview.description ? String(preview.description) : null;
        return (
          <Space direction="vertical" size={2}>
            {title && (
              <Text strong style={{ fontSize: 13 }}>
                {title.length > 60 ? `${title.slice(0, 60)}…` : title}
              </Text>
            )}
            {desc && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {desc.length > 100 ? `${desc.slice(0, 100)}…` : desc}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Signalements',
      dataIndex: 'reportCount',
      key: 'reportCount',
      width: 110,
      sorter: true,
      render: (count: number) => (
        <Text
          strong
          style={{
            color: count >= 5 ? errorColors[400] : undefined,
            fontWeight: 700,
          }}
        >
          {count}
        </Text>
      ),
    },
    {
      title: 'Statut signalements',
      key: 'worstStatus',
      width: 140,
      render: (_: unknown, record: ModerationContentGroup) => (
        <Tag
          style={{
            backgroundColor: `${STATUS_COLORS[record.worstStatus] ?? neutral[300]}14`,
            color: STATUS_COLORS[record.worstStatus] ?? neutral[500],
            borderColor: `${STATUS_COLORS[record.worstStatus] ?? neutral[300]}40`,
            borderRadius: 100,
          }}
        >
          {STATUS_LABELS[record.worstStatus] ?? record.worstStatus}
        </Tag>
      ),
    },
    {
      title: 'Modération',
      key: 'moderationStatus',
      width: 100,
      render: (_: unknown, record: ModerationContentGroup) => (
        <Tag
          style={{
            backgroundColor: `${record.moderationStatus === 'HIDDEN' ? errorColors[400] : neutral[500]}14`,
            color: record.moderationStatus === 'HIDDEN' ? errorColors[400] : neutral[500],
            borderColor: `${record.moderationStatus === 'HIDDEN' ? errorColors[400] : neutral[500]}40`,
            borderRadius: 100,
          }}
        >
          {record.moderationStatus === 'HIDDEN' ? 'Masqué' : 'Visible'}
        </Tag>
      ),
    },
    {
      title: 'Dernier signalement',
      dataIndex: 'latestReportAt',
      key: 'latestReportAt',
      width: 130,
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: ModerationContentGroup) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setSelectedGroup(record)}
        >
          Examiner
        </Button>
      ),
    },
  ];

  const pendingSubtitle = data?.total
    ? `${data.total} signalement${data.total > 1 ? 's' : ''}`
    : undefined;

  return (
    <div>
      <PageHeader
        title="Modération du contenu"
        subtitle={pendingSubtitle}
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="Statut"
          allowClear
          style={{ width: 150 }}
          value={filters.status}
          onChange={(v) => setFilters((prev) => ({ ...prev, status: v, page: 1 }))}
          options={[
            { value: 'PENDING', label: 'En attente' },
            { value: 'REVIEWED', label: 'Examiné' },
            { value: 'RESOLVED', label: 'Résolu' },
            { value: 'DISMISSED', label: 'Rejeté' },
          ]}
        />
        <Select
          placeholder="Type de contenu"
          allowClear
          style={{ width: 200 }}
          value={filters.reportableType}
          onChange={(v) => setFilters((prev) => ({ ...prev, reportableType: v, page: 1 }))}
          options={[
            { value: 'POST', label: 'Publication' },
            { value: 'NEIGHBORING_REPORT', label: 'Signalement voisinage' },
            { value: 'NEIGHBORING_REPORT_COMMENT', label: 'Commentaire' },
          ]}
        />
        <Select
          placeholder="Motif"
          allowClear
          style={{ width: 200 }}
          value={filters.reason}
          onChange={(v) => setFilters((prev) => ({ ...prev, reason: v, page: 1 }))}
          options={Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Select
          style={{ width: 180 }}
          value={`${filters.sortBy}:${filters.sortOrder}`}
          onChange={(v) => {
            const [sortBy, sortOrder] = v.split(':') as [ModerationFilters['sortBy'], 'asc' | 'desc'];
            setFilters((prev) => ({ ...prev, sortBy, sortOrder }));
          }}
          options={[
            { value: 'reportCount:desc', label: 'Plus signalés' },
            { value: 'reportCount:asc', label: 'Moins signalés' },
            { value: 'createdAt:desc', label: 'Plus récents' },
            { value: 'createdAt:asc', label: 'Plus anciens' },
          ]}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={groups}
        loading={isLoading}
        rowKey="key"
        pagination={{
          current: filters.page,
          pageSize: filters.limit,
          total: data?.total,
          onChange: (page) => updateFilter('page', page),
          showSizeChanger: false,
        }}
        locale={{ emptyText: 'Aucun signalement' }}
      />

      <ReportDetailDrawer
        group={selectedGroup}
        open={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
      />
    </div>
  );
}
